import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import React from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import CrossIcon from '../../img/icons/Cross';
import { hasPermission } from '../../store/apiWrapper';
import { chartActions } from '../../store/charts/chartsStore';
import { GRAPH_MODE } from '../../store/charts/common/constants';
import { domainActions } from '../../store/domainStore';
import { popupActions } from '../../store/popupStore';
import { validateInput } from '../../store/storeFunctions';
import BbDatePicker from '../common/datePicker';
import PopupMultiSelectButton from '../common/popupMultiSelectButton';
import BbCheckbox from '../styling/bbCheckbox';
import ChartSettingsBox from './chartSettings/chartSettingsBox';
import ChartSettingsButton from './chartSettings/chartSettingsButton';

import './chartSettings.scss';

class ChartSettings extends React.Component {
    state = {
        collapseAll: true
    };

    get settings() {
        return this.props.leftPanel;
    }

    render() {
        return (
            <article className="panel">
                <div className="panel-heading">
                    <div className="panel-heading-title">
                        <div style={{ marginRight: "auto", marginTop: "auto", marginBottom: "auto" }}>
                            <Translate id="Report.Settings.Title" />
                        </div>
                        <CrossIcon className="icon-centered" onClick={this.props.closePanel} style={{ marginLeft: "0.5em" }} />
                    </div>
                </div>
                <div className="panel-block rc-panel">
                    <div className="rc-collapse-all">
                        <div className="rc-collapse-all-title" onClick={() => this.collapseAll()}>
                            <span>
                                <Translate id="Common.ShowHideAll" />
                            </span>
                            <FontAwesomeIcon className="svg-standard-color rc-expand-indicator" icon={this.state.collapseAll ? "angle-up" : "angle-down"} />
                        </div>
                    </div>
                    {this.renderBody()}
                </div>
            </article>
        );
    }

    componentDidUpdate(_, prevState) {
        if (!isEqual(this.state, prevState)) {
            const keys = Object.keys(this.state).filter(f => f.startsWith("expanded"));
            if (keys.every(k => this.state[k] === true))
                this.setState({ collapseAll: true });
            else if (keys.every(k => this.state[k] === false))
                this.setState({ collapseAll: false });
        }
    }

    renderBody = () => {
        return (
            <div id="leftSidePanelWraper" data-html2canvas-ignore
                style={{ marginLeft: "auto", height: "100%", overflowY: "auto", overflowX: "hidden", width: "100%" }}
                className={this.props.leftPanel.expanded ? "expanded" : ""}
            >
                <div id="leftSidePanel" className="sidePanel" style={{ display: this.props.leftPanel.expanded ? "block" : "none" }}>
                    {this.renderLevelButtons()}
                    {this.renderLockButtons()}
                    {this.renderWindowButtons()}
                    {this.renderApplyFilters()}
                    {this.renderDatePickers()}
                    {this.renderRelationDirections()}
                    {this.renderPropertyCounter()}
                    {hasPermission(null, "CustomEntitiesAndRelations") && !this.props.user.authentication.limitedUser && this.renderAddCustomRelations()}
                    {this.renderRelationTypesFilter()}
                    {hasPermission(null, "WarningsBC") && this.renderWarningTypesFilter()}
                </div>
            </div>
        );
    }

    renderLevelButtons = () => {
        return (
            <ChartSettingsBox id="level"
                header={this.props.translate("Charts.Level")}
                disabled={this.props.mode === GRAPH_MODE.path}
                expanded={this.state.expandedLevel}
                onExpand={(v) => this.setState({ expandedLevel: v })}
            >
                {[1, 2, 3, 4].map(level => <ChartSettingsButton key={level}
                    type={`level${level}`}
                    toggled={this.props.leftPanel.level === level}
                    disabled={this.props.mode === GRAPH_MODE.path}
                    action={() => this.props.changeLevel(level, this.props.mode)}
                />)}
            </ChartSettingsBox>
        );
    }

    renderLockButtons = () => {
        return (
            <ChartSettingsBox id="lock"
                header={this.props.translate("Charts.LockUnlockAll")}
                disabled={this.props.mode === GRAPH_MODE.path}
                expanded={this.state.expandedLock}
                onExpand={(v) => this.setState({ expandedLock: v })}
            >
                <ChartSettingsButton type="lock"
                    toggled={this.props.mode !== GRAPH_MODE.path && this.settings.lockedAll === true}
                    disabled={this.props.mode === GRAPH_MODE.path}
                    action={() => this.props.lockAll(true)}
                />
                <ChartSettingsButton type="unlock"
                    toggled={this.props.mode !== GRAPH_MODE.path && this.settings.lockedAll === false}
                    disabled={this.props.mode === GRAPH_MODE.path}
                    action={() => this.props.lockAll(false)}
                />
            </ChartSettingsBox>
        );
    }

    renderWindowButtons = () => {
        return (
            <ChartSettingsBox id="windows" header={this.props.translate("Charts.Windows")}
                disabled={this.props.mode === GRAPH_MODE.path}
                expanded={this.state.expandedWindows}
                onExpand={(v) => this.setState({ expandedWindows: v })}
            >
                <ChartSettingsButton type="negativeInfo"
                    toggled={this.props.mode !== GRAPH_MODE.path && this.props.bottomWindow.negInfo.isShowed}
                    disabled={this.props.mode === GRAPH_MODE.path}
                    action={() => this.props.toggleBottomWindow("negInfo")}
                />
                <ChartSettingsButton type="timeline"
                    toggled={this.props.mode !== GRAPH_MODE.path && this.props.bottomWindow.timeline.isShowed}
                    disabled={this.props.mode === GRAPH_MODE.path}
                    action={() => this.props.toggleBottomWindow("timeline")}
                />
                <ChartSettingsButton type="subjectList"
                    toggled={this.props.mode !== GRAPH_MODE.path && this.props.bottomWindow.subjects.isShowed}
                    disabled={this.props.mode === GRAPH_MODE.path}
                    action={() => this.props.toggleBottomWindow("subjects")}
                />
            </ChartSettingsBox>
        );
    }

    renderApplyFilters = () => {
        const disabled = this.props.mode === GRAPH_MODE.path || (this.props.mode !== GRAPH_MODE.path && !this.settings.filterChanged);
        return (
            <ChartSettingsBox id="apply" header={this.props.translate("Charts.FilterOptions")} expandable={false} contentClasses={{ "rc-center": true }}>
                <button className={classnames("bb-button small",
                    { "gradient-1": !disabled, "gradient-1-gray": disabled, "is-square": localStorage.locales_button_style === "square" })}
                    disabled={disabled}
                    onClick={() => this.applyFilter()}
                >
                    <span><FontAwesomeIcon icon="exclamation-triangle" /> <strong><Translate id="Charts.UseFilter" /></strong></span>
                </button>
            </ChartSettingsBox>
        );
    }

    renderDatePickers = () => {
        const fontSize = localStorage.font_size;
        const dateOverlayStyle = { right: fontSize === "12px" ? "-5.5em" : fontSize === "14px" ? "-3.5em" : "-2.5em" };
        return (
            <ChartSettingsBox id="historical-relations"
                header={this.props.translate("Charts.HistoricalRelations")}
                disabled={this.props.mode === GRAPH_MODE.path}
                popupButtonDisabled={this.props.mode === GRAPH_MODE.path || (this.props.mode !== GRAPH_MODE.path && !this.settings.filterChanged)}
                showChangeIndicator={this.settings.historyChanged}
                onApplyFilter={() => this.applyFilter()}
                contentClasses={{ "rc-no-flex": true }}
                toggle={{
                    id: "historical-relations",
                    value: this.settings.history,
                    onChange: this.props.changeHistory
                }}
                expanded={this.state.expandedHistoricalRelations}
                onExpand={(v) => this.setState({ expandedHistoricalRelations: v })}
            >
                <table className="rc-hist-relations-table">
                    <tbody>
                        <tr>
                            <td><Translate id="Common.From" /></td>
                            <td className="rc-date-picker">
                                <BbDatePicker id="dateFrom"
                                    enableReinitialize={true}
                                    afterDateChange={(d) => this.props.histDateChange(0, d)}
                                    value={this.settings.dateFrom}
                                    style={{ maxWidth: "12em", margin: "0 auto" }}
                                    yearsBack={120}
                                    disabled={!this.settings.history}
                                    overlayStyle={dateOverlayStyle}
                                    rightPanel
                                />
                            </td>
                        </tr>
                        <tr>
                            <td><Translate id="Common.To" /></td>
                            <td className="rc-date-picker">
                                <BbDatePicker id="dateTo"
                                    enableReinitialize={true}
                                    afterDateChange={(d) => this.props.histDateChange(1, d)}
                                    value={this.settings.dateTo}
                                    style={{ maxWidth: "12em", margin: "0 auto" }}
                                    yearsBack={120}
                                    disabled={!this.settings.history}
                                    overlayStyle={dateOverlayStyle}
                                    rightPanel
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </ChartSettingsBox>
        );
    }

    renderRelationDirections = () => {
        return (
            <ChartSettingsBox id="relation-directions" header={this.props.translate("Charts.LinkDirection")}
                showChangeIndicator={this.settings.directionChanged}
                onApplyFilter={() => this.applyFilter()}
                expanded={this.state.expandedRelationDirections}
                popupButtonDisabled={this.props.mode === GRAPH_MODE.path || (this.props.mode !== GRAPH_MODE.path && !this.settings.filterChanged)}
                onExpand={(v) => this.setState({ expandedRelationDirections: v })}
            >
                <ChartSettingsButton type="relationsTwoWay" toggled={this.settings.linkDirection === 0} action={() => this.props.changeDirection(0)} />
                <ChartSettingsButton type="relationsFrom" toggled={this.settings.linkDirection === -1} action={() => this.props.changeDirection(-1)} />
                <ChartSettingsButton type="relationsTo" toggled={this.settings.linkDirection === 1} action={() => this.props.changeDirection(1)} />
            </ChartSettingsBox>
        );
    }

    renderPropertyCounter = () => {
        return (
            <ChartSettingsBox id="ownership-interest"
                header={this.props.translate("Charts.OwnershipInterests")}
                popupButtonDisabled={this.props.mode === GRAPH_MODE.path || (this.props.mode !== GRAPH_MODE.path && !this.settings.filterChanged)}
                showChangeIndicator={this.settings.shareChanged}
                onApplyFilter={() => this.applyFilter()}
                contentClasses={{ "rc-property-counter": true }}
                toggle={{
                    id: "property-counter-chk",
                    value: this.settings.shareChecked,
                    onChange: this.changeShare
                }}
                expanded={this.state.expandedOwnershipInterests}
                onExpand={(v) => this.setState({ expandedOwnershipInterests: v })}
            >
                <ChartSettingsButton type="sharePlus" action={() => this.props.changeShare(this.settings.shareValue + 5, this.settings.shareChecked)} />
                <div className="rc-property-counter-input">
                    <input type="text"
                        id="counter-property"
                        className="input form-control"
                        value={`${this.settings.shareValue}%`}
                        disabled={!this.settings.shareChecked}
                        onChange={(e) => this.props.changeShare(e.target.value, this.settings.shareChecked)}
                        onInput={(e) => validateInput(e)}
                    />
                </div>
                <ChartSettingsButton type="shareMinus" action={() => this.props.changeShare(this.settings.shareValue - 5, this.settings.shareChecked)} />
            </ChartSettingsBox>
        );
    }

    renderAddCustomRelations = () => {
        const disabled = !this.settings.customRelationsEnabled;
        return (
            <ChartSettingsBox id="add-custom-relations" contentClasses={{ "rc-center": true }}>
                <button className={classnames("bb-button small",
                    { "gradient-1": !disabled, "gradient-1-gray": disabled, "is-square": localStorage.locales_button_style === "square" })}
                    disabled={disabled}
                    onClick={() => this.props.addCustomRelationClick()}
                >
                    <Translate id="Charts.AddRelation" />
                </button>
            </ChartSettingsBox>
        );
    }

    renderRelationTypesFilter = () => {
        return (
            <ChartSettingsBox id="relation-types"
                header={this.props.translate("Charts.ShowingRelationTypes")}
                popupButtonDisabled={this.props.mode === GRAPH_MODE.path || (this.props.mode !== GRAPH_MODE.path && !this.settings.filterChanged)}
                showChangeIndicator={this.settings.relationTypesChanged}
                onApplyFilter={() => this.applyFilter()}
                contentClasses={{ "rc-no-flex": true }}
                expanded={this.state.expandedRelationTypes}
                onExpand={(v) => this.setState({ expandedRelationTypes: v })}
            >
                <div className="columns rc-box-row">
                    <div className="rc-checkbox">
                        <BbCheckbox id="chbRelTypeAll"
                            label={this.props.translate("Charts.All")}
                            value={this.settings.relTypesAllChecked ? true : this.settings.relTypesAllPartial ? null : false}
                            onChange={(v) => this.rtAllChanged(v)}
                        />
                    </div>
                </div>
                {this.props.domains.relationTypes.map((rt) => this.renderFilterCheckBox(rt, "chbRelType", this.isRtChecked, this.rtCheckChanged))}
                <div className="rc-detailed-selection">
                    <PopupMultiSelectButton asLink
                        title={this.props.translate("Charts.ShowingRelationTypes")}
                        linkText={this.props.translate("Charts.DetailedSelection")}
                        customDomain={this.markNotContainedRelTypes(this.props.domains.relationTypes)}
                        popupSize="small"
                        selection={{ all: this.props.leftPanel.rt.map((r) => ({ value: r })) }}
                        onScroll={(e) => this.onScroll(e)}
                        handleChange={(all, _) => this.props.changeRelationTypes(all)}
                        paddingLeft={true}
                    />
                </div>
            </ChartSettingsBox>
        );
    }

    renderWarningTypesFilter = () => {
        return (
            <ChartSettingsBox id="warning-types"
                header={this.props.translate("Charts.WarningTableSubjectNegInfo")}
                contentClasses={{ "rc-no-flex": true }}
                expanded={this.state.expandedWarningTypes}
                onExpand={(v) => this.setState({ expandedWarningTypes: v })}
            >
                <div className="columns rc-box-row">
                    <div className="rc-checkbox">
                        <BbCheckbox id="chbWarTypeAll"
                            label={this.props.translate("Charts.All")}
                            value={this.settings.warTypesAllChecked ? true : this.settings.warTypesAllPartial ? null : false}
                            onChange={(v) => this.wtAllChanged(v)}
                        />
                    </div>
                </div>
                {this.props.domains.warningTypes.map((wt) => this.renderFilterCheckBox(wt, "chbWarType", this.isWtChecked, this.wtCheckChanged))}
                <div className="rc-detailed-selection">
                    <PopupMultiSelectButton
                        title={this.props.translate("Charts.WarningType")}
                        asLink={true}
                        linkText={this.props.translate("Charts.DetailedSelection")}
                        customDomain={this.markNotContainedWarTypes(this.props.domains.warningTypes)}
                        popupSize="small"
                        selection={{ all: this.settings.wt.map((r) => ({ value: r })) }}
                        onScroll={(e) => this.onScroll(e)}
                        handleChange={(all, _) => this.props.changeWarningTypes(all)}
                        paddingLeft={true}
                    />
                </div>
            </ChartSettingsBox>
        );
    }

    renderFilterCheckBox = (cb, idPrefix, valueFn, onChangeFn) => {
        return (
            <div className="columns rc-box-row" key={`${idPrefix}${cb.value}`}>
                <div className="rc-checkbox">
                    <BbCheckbox id={`${idPrefix}${cb.value}`}
                        label={cb.label}
                        value={valueFn(cb.value)}
                        onChange={(v) => onChangeFn(v, cb.value)}
                    />
                </div>
            </div>
        );
    }

    collapseAll = () => {
        const newState = {};
        Object.keys(this.state).filter(f => f.startsWith("expanded")).forEach(k => newState[k] = !this.state.collapseAll);
        this.setState({ ...newState, collapseAll: !this.state.collapseAll });
    }

    isRtChecked = (id) => {
        if (this.settings.rt.indexOf(id) !== -1)
            return true;
        for (var i = 0; i < this.settings.rt.length; i++)
            if (this.settings.rt[i].startsWith(id))
                return null;    //partial
        return false;
    }
    isRtNotContained = (id) => this.settings.rtUsed.filter(f => f.startsWith(id)).length === 0 ? "not-contained" : "";
    rtAllChanged = (value) => this.props.allRelTypes(value);

    rtCheckChanged = (value, id) => {
        if (value)
            this.props.addRelType(id);
        else
            this.props.removeRelType(id);
    }

    markNotContainedRelTypes = (rt) => {
        rt.forEach(r => {
            r.notContained = this.settings.rtUsed.filter(function (f) { return f.startsWith(r.value) }).length === 0;
            r.children = this.markNotContainedRelTypes(r.children);
        });
        return rt;
    }

    isWtChecked = (id) => {
        if (this.settings.wt.indexOf(id) !== -1)
            return true;
        for (var i = 0; i < this.settings.wt.length; i++)
            if (this.settings.wt[i].startsWith(id))
                return null;    //partial
        return false;
    }

    isWtNotContained = (id) => this.settings.wtUsed.filter(function (f) { return f.startsWith(id) }).length === 0 ? "not-contained" : "";
    wtAllChanged = (value) => this.props.allWarTypes(value);

    wtCheckChanged = (value, id) => {
        if (value)
            this.props.addWarType(id);
        else
            this.props.removeWarType(id);
    }

    markNotContainedWarTypes = (wt) => {
        wt.forEach(w => {
            w.notContained = this.settings.wtUsed.filter(function (f) { return f.startsWith(w.value) }).length === 0;
            w.children = this.markNotContainedWarTypes(w.children);
        });
        return wt;
    }

    changeShare = (value) => this.props.changeShare(this.settings.shareValue, value);

    applyFilter = () => {
        if (this.settings.history) {
            if (document.getElementById('dateFrom').classList.contains('is-danger'))
                return false;
            if (document.getElementById('dateTo').classList.contains('is-danger'))
                return false;
        }
        this.props.applyFilter();
        return true;
    }
}

export default withRouter(withLocalize(connect(
    state => ({ ...state.charts, popup: state.popup, ...state.domain, user: state.userContext, layout: state.layout }),
    dispatch => bindActionCreators(({ ...chartActions, ...popupActions, ...domainActions }), dispatch)
)(ChartSettings)));
